/* eslint-disable @typescript-eslint/no-explicit-any */
import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DataGrid, { DataGridConfig } from 'components/DataGrid';
import IconButton from 'components/IconButton';
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useState, Suspense, useMemo } from 'react';
import Select from 'react-select';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { findingGridDataRefreshAtom } from './state';

const RowActions = ({
  rowData,
  onLoading,
  setExistingWatchers,
  members,
  setWatcherOptions,
  finding,
  refreshFindingsGridData,
}) => {
  const handleRemoveWatcher = async () => {
    onLoading(true);
    callApi(
      `/assignee_maps/watchers/${rowData?.id}/`,
      'PATCH',
      {},
      { is_active: false },
    )
      .then((res) => {
        if (res.status === 200) {
          // Remove the watcher from the existingWatchers state
          setExistingWatchers((prevWatchers) =>
            prevWatchers.filter((watcher) => watcher.id !== rowData.id),
          );
          // Add removed user back to the members list
          let removedWatcher = members.find(
            (watcher) => watcher.users_id === rowData.user,
          );
          setWatcherOptions((watcher_options) => [
            ...watcher_options,
            removedWatcher,
          ]);
          finding['watcher_count'] = finding['watcher_count'] - 1;
          showSuccessToast(
            `Member has been successfully removed from watchers list`,
          );
          refreshFindingsGridData(Math.random().toString());
        }
      })
      .catch((err) => {
        showFailureToast(
          `Failed to remove watcher. ${err?.statusText}: ${err?.data?.error}`,
        );
      })
      .finally(() => {
        onLoading(false);
      });
  };

  return (
    <div>
      <IconButton
        id="vulnLink"
        iconClass="fa fa-user-times"
        size="sm"
        tooltip="Remove Watcher"
        onClick={handleRemoveWatcher}
      />
    </div>
  );
};

interface WatcherModalProps {
  finding: any;
  isOpen: boolean;
  toggle: () => void;
}

const WatcherModal: React.FC<WatcherModalProps> = ({
  isOpen,
  finding: finding,
  toggle,
}) => {
  const company_id = useAtomValue(companyIdAtom);
  const [loading, setLoading] = useState(false);
  const [existingWatchers, setExistingWatchers] = useState<any[]>([]);
  const [currentWatcher, setCurrentWatcher] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [watchersOptions, setWatchersOptions] = useState<any[]>([]);
  const refreshFindingsGridData = useSetAtom(findingGridDataRefreshAtom);

  const findingId = finding.id;
  const sourceType = finding.source_type;
  const title = finding.name;
  const qid = finding.qid;
  const ruleId = finding.sonarqube_rule;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch both members and watchers in parallel
        const [watchersRes, membersRes] = await Promise.all([
          callApi(
            `/assignee_maps/watchers/?finding_id=${findingId}`,
            'GET',
            {},
          ),
          callApi(
            `/profiles/colleague/new/?search=&is_active=True&nutshell=True&company_id=${company_id}`,
            'GET',
            {},
          ),
        ]);
        // Process and set data
        const fetchedWatchers =
          watchersRes.status === 200 ? watchersRes.data.results : [];
        const fetchedMembers =
          membersRes.status === 200 ? membersRes.data.results : [];
        setExistingWatchers(fetchedWatchers);
        // Filter members by excluding existing watchers
        const existingWatcherIds = new Set(
          fetchedWatchers.map((watcher) => watcher.user),
        );
        setMembers(fetchedMembers);
        const filteredMembers = fetchedMembers.filter(
          (member) => !existingWatcherIds.has(member.users_id),
        );
        setWatchersOptions(filteredMembers);
      } catch (err) {
        showFailureToast(`Failed to remove watcher. ${err}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company_id]);

  const getAccountConfig = (): DataGridConfig => ({
    rowIdKey: 'id',
    columns: [
      { key: 'id', label: 'ID' },
      {
        key: 'name',
        label: 'Name',
        overrideContents: (rowData) =>
          rowData['first_name'] + ' ' + rowData['last_name'],
      },
      {
        key: 'email',
        label: 'Email',
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (rowData) => (
          <RowActions
            rowData={rowData}
            onLoading={setLoading}
            setExistingWatchers={setExistingWatchers}
            members={members}
            setWatcherOptions={setWatchersOptions}
            finding={finding}
            refreshFindingsGridData={refreshFindingsGridData}
          />
        ),
        headerCellStyle: {
          textAlign: 'right',
          maxWidth: 'auto',
          minWidth: '150px !important',
        },
        rowCellStyle: {
          textAlign: 'right',
          minWidth: '150px !important',
          maxWidth: 'auto',
          whiteSpace: 'nowrap',
        },
      },
    ],
    columnsDisplayConfig: {
      enabledColumnKeys: ['name', 'email', 'actions'],
    },
  });

  const config = useMemo<DataGridConfig>(
    () => getAccountConfig(),
    [existingWatchers, members],
  );

  const saveWatcherList = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    if (!currentWatcher || currentWatcher.length === 0) {
      showFailureToast('No user selected');
      setLoading(false);
      return;
    }
    let vuln_id = findingId;
    let user_ids = currentWatcher.map((watcher) => watcher.id);

    let url = `assignee_maps/add_watchers?user=${user_ids}&finding=${vuln_id}`;
    await callApi(url, 'GET', {})
      .then((res) => {
        if (res.status === 201 || res.status === 200) {
          if (res.data) {
            // Update the existing watchers state
            const addedWatchers = currentWatcher.map((watcher) => ({
              ...watcher,
              user: watcher.id,
            }));
            setExistingWatchers((prev) => [...prev, ...addedWatchers]);
            // Remove added watchers from the members state
            setWatchersOptions((prevWatchersOptions) =>
              prevWatchersOptions.filter(
                (watcher) => !user_ids.includes(watcher?.id),
              ),
            );
            finding['watcher_count'] =
              finding['watcher_count'] + res.data.watchers.length;
            // Clear the selected watchers
            setCurrentWatcher([]);
            showSuccessToast(
              'The User has been successfully added to the watchers list.',
            );
            refreshFindingsGridData(Math.random().toString());
            toggle();
          }
        }
      })
      .catch((err) => {
        showFailureToast(
          `Failed to remove watcher. ${err?.statusText}: ${err?.data?.error}`,
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>Manage Vulnerability Watchers</ModalHeader>
      <ModalBody>
        <Row>
          <Col md="12">
            {' '}
            <label>
              <p>
                <strong>Name: </strong>
                {title}
              </p>
            </label>
          </Col>
          <Col md="6">
            <label>
              <p>
                <strong>Finding ID: </strong>
                {findingId}
              </p>
            </label>
          </Col>
          {sourceType === 'sast' ? (
            <Col md="6">
              <label>
                <p>
                  <strong>Rule ID: </strong>
                  {ruleId}
                </p>
              </label>
            </Col>
          ) : (
            <Col md="6">
              <label>
                <p>
                  <strong>QID: </strong>
                  {qid}
                </p>
              </label>
            </Col>
          )}
        </Row>
        <Row>
          <Col md="12">
            <CustomModalWrapper id="existing-notes-scroll" height="40vh">
              <Suspense fallback={<></>}>
                <DataGrid
                  config={config}
                  data={existingWatchers}
                  loading={loading}
                />
              </Suspense>
            </CustomModalWrapper>
          </Col>
        </Row>
        <Row>
          <Col sm="12">Add new watcher</Col>
          <Col sm="12">
            <Select
              className="custom-360-dropdown profile-pic-dropdown"
              closeMenuOnSelect={false}
              isMulti
              isClearable={true}
              hideSelectedOptions={true}
              onChange={(e) => {
                setCurrentWatcher(e as any);
              }}
              value={currentWatcher}
              options={watchersOptions}
              styles={{
                control: (base) => ({
                  ...base,
                  cursor: 'pointer',
                }),
                option: (base) => ({
                  ...base,
                  cursor: 'pointer',
                }),
              }}
            />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={!!loading}>
          Cancel
        </Button>
        {loading ? (
          <CircleSpinner size={20} color="#0665a9" loading={true} />
        ) : (
          <Button
            color="primary"
            disabled={true && currentWatcher.length === 0}
            onClick={saveWatcherList}
          >
            Add Watchers
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default WatcherModal;
